// WorkoutMode.js - Modificado
import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Container, 
  Button, 
  IconButton,
  Grid,
  Divider,
  Card,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Clock,
  Dumbbell,
  Plus,
  Settings,
  XCircle,
  Heart,
  Flame,
  Timer,
  ChevronLeft
} from 'lucide-react';

// Constants
const FIT_ORANGE = '#FF6B00';
const GRADIENT = 'linear-gradient(90deg, #FF9800 0%, #FF6B00 100%)';

// Colores para modo wallpaper
const WALLPAPER_COLORS = {
  bg: 'rgba(128, 128, 128, 0.3)',
  bgLight: 'rgba(128, 128, 128, 0.25)',
  border: 'rgba(128, 128, 128, 0.3)',
  text: '#FFFFFF',
  textSecondary: 'rgba(255, 255, 255, 0.7)'
};

// Styled Components
const WorkoutContainer = styled(Container)(({ theme }) => ({
  padding: theme.spacing(4, 3, 3),
  backgroundColor: theme.palette.actualMode === 'wallpaper'
    ? 'transparent'
    : (theme.palette.mode === 'dark' ? '#000000' : '#FFFFFF'),
  maxWidth: '1200px',
  height: '100%',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(3, 1, 2),
  }
}));

const TopBar = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing(4),
  position: 'sticky',
  top: 0,
  zIndex: 10,
  padding: theme.spacing(2),
  backgroundColor: theme.palette.actualMode === 'wallpaper'
    ? WALLPAPER_COLORS.bg
    : (theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.8)' : 'rgba(255,255,255,0.8)'),
  backdropFilter: 'blur(10px)',
  WebkitBackdropFilter: 'blur(10px)',
  borderRadius: theme.spacing(2),
  border: theme.palette.actualMode === 'wallpaper' ? `1px solid ${WALLPAPER_COLORS.border}` : 'none',
}));

const TimerButton = styled(IconButton)(({ theme }) => ({
  backgroundColor: theme.palette.actualMode === 'wallpaper'
    ? WALLPAPER_COLORS.bgLight
    : (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'),
  backdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
  WebkitBackdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
  border: theme.palette.actualMode === 'wallpaper' ? `1px solid ${WALLPAPER_COLORS.border}` : 'none',
  borderRadius: theme.spacing(2),
  padding: theme.spacing(1),
}));

const FinishButton = styled(Button)(({ theme }) => ({
  borderRadius: 30,
  padding: theme.spacing(1, 3),
  background: GRADIENT,
  color: '#FFFFFF',
  fontWeight: 600,
  boxShadow: 'none',
  textTransform: 'none',
}));

const StatsCard = styled(Card)(({ theme }) => ({
  borderRadius: 16,
  padding: theme.spacing(2),
  backgroundColor: theme.palette.actualMode === 'wallpaper'
    ? WALLPAPER_COLORS.bg
    : (theme.palette.mode === 'dark' ? '#1A1A1A' : '#FFFFFF'),
  backdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(10px)' : 'none',
  WebkitBackdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(10px)' : 'none',
  border: `1px solid ${theme.palette.actualMode === 'wallpaper'
    ? WALLPAPER_COLORS.border
    : (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.03)')}`,
  boxShadow: theme.palette.actualMode === 'wallpaper' ? 'none' : undefined,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
}));

const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: 12,
  padding: theme.spacing(1.5, 3),
  textTransform: 'none',
  fontWeight: 500,
  fontSize: '15px',
  boxShadow: 'none',
  backgroundColor: FIT_ORANGE,
  color: '#FFFFFF',
  '&:hover': {
    backgroundColor: '#F57C00',
    boxShadow: 'none',
  },
}));

const OutlineButton = styled(Button)(({ theme }) => ({
  borderRadius: 12,
  padding: theme.spacing(1.5, 3),
  textTransform: 'none',
  fontWeight: 500,
  fontSize: '15px',
  boxShadow: 'none',
  border: `1px solid ${theme.palette.actualMode === 'wallpaper'
    ? WALLPAPER_COLORS.border
    : (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)')}`,
  backgroundColor: theme.palette.actualMode === 'wallpaper' ? WALLPAPER_COLORS.bgLight : 'transparent',
  backdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
  WebkitBackdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
  color: theme.palette.text.primary,
}));

// Diálogo estilizado
const BlurredDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: 20,
    boxShadow: theme.palette.actualMode === 'wallpaper'
      ? 'none'
      : '0 10px 30px rgba(0, 0, 0, 0.15)',
    backdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(20px)' : 'blur(3px)',
    WebkitBackdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(20px)' : 'blur(3px)',
    backgroundColor: theme.palette.actualMode === 'wallpaper'
      ? WALLPAPER_COLORS.bg
      : (theme.palette.mode === 'dark'
        ? 'rgba(30, 30, 30, 0.85)'
        : 'rgba(255, 255, 255, 0.85)'),
    border: theme.palette.actualMode === 'wallpaper'
      ? `1px solid ${WALLPAPER_COLORS.border}`
      : (theme.palette.mode === 'dark'
        ? '1px solid rgba(255, 255, 255, 0.1)'
        : '1px solid rgba(0, 0, 0, 0.1)'),
  }
}));

// Main Component
const WorkoutMode = ({ onExit, onFinish }) => {
  const theme = useTheme();
  const [duration, setDuration] = useState(0);
  const [isRunning, setIsRunning] = useState(true);
  const [exercises] = useState([]);
  const [heartRate] = useState('--');
  const [calories, setCalories] = useState(0);
  const [openDiscardDialog, setOpenDiscardDialog] = useState(false);
  
  // Timer effect
  useEffect(() => {
    let interval;
    if (isRunning) {
      interval = setInterval(() => {
        setDuration(prev => prev + 1);
        // Simulate calorie burn (roughly 8-10 calories per minute for moderate exercise)
        setCalories(prev => prev + (1/60 * 9));
      }, 1000);
    }
    
    return () => clearInterval(interval);
  }, [isRunning]);
  
  // Format duration to MM:SS
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };
  
  // Format duration to HH:MM:SS for longer workouts
  const formatLongTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours > 0 ? hours + ':' : ''}${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };
  
  const handleAddExercise = () => {
    // In a real app, this would open an exercise selection modal
  };
  
  const handleToggleTimer = () => {
    setIsRunning(prev => !prev);
  };
  
  const handleFinishWorkout = () => {
    if (onFinish) {
      onFinish({
        duration,
        calories: Math.round(calories),
        exercises
      });
    }
  };
  
  const handleOpenDiscardDialog = () => {
    setOpenDiscardDialog(true);
  };
  
  const handleCloseDiscardDialog = () => {
    setOpenDiscardDialog(false);
  };
  
  const handleDiscardWorkout = () => {
    setOpenDiscardDialog(false);
    if (onExit) {
      onExit();
    }
  };

  return (
    <WorkoutContainer>
      <TopBar>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={handleOpenDiscardDialog} sx={{ color: FIT_ORANGE }}>
            <ChevronLeft size={24} />
          </IconButton>
          <TimerButton onClick={handleToggleTimer}>
            <Clock size={24} />
          </TimerButton>
          <Typography variant="h6" fontWeight={600}>
            {formatLongTime(duration)}
          </Typography>
        </Box>
        
        <FinishButton onClick={handleFinishWorkout}>
          Finish
        </FinishButton>
      </TopBar>
      
      {/* Live Stats */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={4}>
          <StatsCard>
            <Box sx={{ mb: 1, color: FIT_ORANGE }}>
              <Heart size={24} />
            </Box>
            <Typography variant="h6" fontWeight={600}>
              {heartRate}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              BPM
            </Typography>
          </StatsCard>
        </Grid>
        
        <Grid item xs={4}>
          <StatsCard>
            <Box sx={{ mb: 1, color: FIT_ORANGE }}>
              <Flame size={24} />
            </Box>
            <Typography variant="h6" fontWeight={600}>
              {Math.round(calories)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              KCAL
            </Typography>
          </StatsCard>
        </Grid>
        
        <Grid item xs={4}>
          <StatsCard>
            <Box sx={{ mb: 1, color: FIT_ORANGE }}>
              <Timer size={24} />
            </Box>
            <Typography variant="h6" fontWeight={600}>
              {formatTime(duration)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              DURATION
            </Typography>
          </StatsCard>
        </Grid>
      </Grid>
      
      <Divider sx={{ mb: 4 }} />
      
      {/* Exercise Section */}
      {exercises.length === 0 ? (
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center',
          justifyContent: 'center',
          py: 8,
          textAlign: 'center'
        }}>
          <Dumbbell size={64} color={FIT_ORANGE} strokeWidth={1.5} />
          <Typography variant="h5" fontWeight={600} sx={{ mt: 3, mb: 1 }}>
            Get Started
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 400 }}>
            Add an exercise to start your workout
          </Typography>
          
          <ActionButton
            startIcon={<Plus size={18} />}
            onClick={handleAddExercise}
          >
            Add Exercise
          </ActionButton>
        </Box>
      ) : (
        <Box>
          {/* This would contain the list of exercises */}
          <Typography variant="h6">Exercise List (to be implemented)</Typography>
        </Box>
      )}
      
      {/* Bottom Buttons */}
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        mt: 4,
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        padding: 3,
        backgroundColor: theme => theme.palette.actualMode === 'wallpaper'
          ? WALLPAPER_COLORS.bg
          : (theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.8)' : 'rgba(255,255,255,0.8)'),
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
        border: theme => theme.palette.actualMode === 'wallpaper' ? `1px solid ${WALLPAPER_COLORS.border}` : 'none',
      }}>
        <OutlineButton
          startIcon={<Settings size={18} />}
        >
          Settings
        </OutlineButton>
        
        <OutlineButton
          startIcon={<XCircle size={18} />}
          onClick={handleOpenDiscardDialog}
          sx={{ color: 'error.main', borderColor: 'error.main' }}
        >
          Discard Workout
        </OutlineButton>
      </Box>
      
      {/* Discard Dialog */}
      <BlurredDialog
        open={openDiscardDialog}
        onClose={handleCloseDiscardDialog}
        aria-labelledby="discard-workout-dialog-title"
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle id="discard-workout-dialog-title" sx={{ textAlign: 'center', pt: 3 }}>
          Discard Workout?
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <XCircle size={48} color={theme.palette.error.main} />
          </Box>
          <Typography variant="body1" align="center">
            Are you sure you want to discard this workout? Your progress will not be saved.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 3 }}>
          <Button 
            onClick={handleCloseDiscardDialog} 
            sx={{ 
              color: theme.palette.text.secondary,
              textTransform: 'none',
              fontWeight: 500
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDiscardWorkout} 
            sx={{ 
              color: theme.palette.error.main,
              textTransform: 'none',
              fontWeight: 500
            }}
          >
            Discard Workout
          </Button>
        </DialogActions>
      </BlurredDialog>
    </WorkoutContainer>
  );
};

export default WorkoutMode;